import * as React from "react";
import {
    HashRouter as Router,
    Routes,
    Route
} from "react-router-dom";

import {
    initFormSubmit,
    mapFormValuesToModel,
    mapModelToFormValues,
    contactInfoNameStepHasServerSideErrors,
    contactInfoAddressStepHasServerSideErrors,
    redirectToRoutePath,
    initCaptureQuoteFormUnload,
    cleanUpCaptureQuoteFormUnload,
    getRoutePath,
} from "../common/Utils";

import { mapPreFilledQuoteModelToFormValues, getResumeStepFromPreFilledValues } from "../common/QuoteFormPreFill";

import { IAutoWizardModel } from "../interfaces/IAutoWizardModel";
import { PreFilledQuoteModel } from "../interfaces/PreFilledQuoteModel";

import { VehicleYearStep } from "./VehicleYearStep";
import { VehicleMakeStep } from "./VehicleMakeStep";
import { VehicleModelStep } from "./VehicleModelStep";

import { AddSecondVehicleStep } from "./AddSecondVehicleStep";
import { AdditionalVehicleYearStep } from "./AdditionalVehicleYearStep";
import { AdditionalVehicleMakeStep } from "./AdditionalVehicleMakeStep";
import { AdditionalVehicleModelStep } from "./AdditionalVehicleModelStep";

import { InsuranceCarrierStep } from "./InsuranceCarrierStep";
import { InsuredSinceStep } from "./InsuredSinceStep";
import { GenderStep } from "./GenderStep";
import { CreditHistoryStep } from "./CreditHistoryStep";
import { LicenseStatusStep } from "./LicenseStatusStep";
import { IncidentsStep } from "./IncidentsStep";
import { MaritalStatusStep } from "./MaritalStatusStep";
import { IsMilitaryStep } from "./IsMilitaryStep";
import { HomeownerStep } from "./HomeownerStep";

import { BundleStep } from "./BundleStep";
import { YearBuiltStep } from "./YearBuiltStep";
import { PropertyTypeStep } from "./PropertyTypeStep";
import { AnyClaimsStep } from "./AnyClaimsStep";
import { InterestedInFloodInsuranceStep } from "./InterestedInFloodInsuranceStep";

import { InterestedInPetInsuranceStep } from "./InterestedInPetInsuranceStep";

import { BirthYearStep } from "./BirthYearStep";
import { BirthMonthStep} from "./BirthMonthStep";
import { BirthDayStep } from "./BirthDayStep";
import { ContactInfoNameStep } from "./ContactInfoNameStep";

import { AdditionalDriverStep } from "./AdditionalDriverStep";
import { AdditionalDriverBirthDayStep } from "./AdditionalDriverBirthDayStep";
import { AdditionalDriverBirthMonthStep } from "./AdditionalDriverBirthMonthStep";
import { AdditionalDriverBirthYearStep } from "./AdditionalDriverBirthYearStep";
import { AdditionalDriverNameStep } from "./AdditionalDriverNameStep";

import { ContactInfoAddressStep } from "./ContactInfoAddressStep";

import { PreFilledQuoteStep } from "./PreFilledQuoteStep";

import { QuoteFormProgressBar } from "../../../Common/ProgressBar/QuoteFormProgressBar";

import {
    SavePrimaryVehicleData, SaveAdditionalVehicleData,
    SavePrimaryDriverData, SaveCurrentInsuranceData,
    SaveHomeAddonData, SavePrimaryDriverBirthDateData,
    SaveContactInfoNameData,
    SaveApplicantInfoData,
    SaveIncidentsData,
    SaveAdditionalDriverData
} from "./SaveConsumerData";
import { WizardStep } from "../common/WizardStep";

export interface AppProps { variation?: string }
export interface AppState { model?: IAutoWizardModel }

declare const preFilledQuoteData: PreFilledQuoteModel;
declare const awl: any;

export default class App extends React.Component<AppProps, AppState> {

    public state: AppState;

    public vehicleMakes: string[] = [];
    public vehicleModels: string[] = [];

    constructor(props: AppProps) {
        super(props);

        if (preFilledQuoteData) {
            mapPreFilledQuoteModelToFormValues(preFilledQuoteData);
        } else {
            this.resumeQuoteForm();
        }

        this.state = {
            model: mapFormValuesToModel(),
        };

        initFormSubmit();
    }

    public componentDidMount() {
        if (preFilledQuoteData) {
            const resumeStep: WizardStep = getResumeStepFromPreFilledValues(preFilledQuoteData);

            // There is data to pre-fill
            redirectToRoutePath(getRoutePath(resumeStep));
        } 

        this.redirectIfServerSideErrorsExist();
        initCaptureQuoteFormUnload();
    }

    public resumeQuoteForm() {
        if (awl.autoWizard.supportsSessionResume) {
            const retrievedResumeData = localStorage.getItem('resumeDataAutoWizard');

            if (retrievedResumeData != null) {
                const resumeData = JSON.parse(retrievedResumeData);

                const resumeQuoteForm = fetch(awl.autoWizard.resumeQuoteFormUrl + "?resumeActionName=" + resumeData.resumeActionName);

                resumeQuoteForm
                    .then((response) => response.json())
                    .then(response => {
                        if (response.redirectUrl != null) {

                            window.location.href = response.redirectUrl;

                            this.setState({ model: resumeData });

                            mapModelToFormValues(resumeData);

                            const resumeNotification = document.getElementById("resume-notification");

                            resumeNotification.style.display = "block";
                        }
                    });
            }
        }
    }

    public componentWillUnmount() {
        cleanUpCaptureQuoteFormUnload();
    }

    public setVehicleMakes = (vehicleMakes: string[]) => {
        this.vehicleMakes = vehicleMakes;
    }

    public setVehicleModels = (vehicleModels: string[]) => {
        this.vehicleModels = vehicleModels;
    }

    private handleUpdateModel = (model: IAutoWizardModel) => {
        mapModelToFormValues(model);
        this.setState({ model: model });
    }
    
    private redirectIfServerSideErrorsExist() {
        if (contactInfoNameStepHasServerSideErrors("ContactInfo_FirstName", "ContactInfo_LastName","ContactInfo_Email")) {
            redirectToRoutePath("/ContactInfoName");
        }

        if (contactInfoAddressStepHasServerSideErrors("ContactInfo_Address", "ContactInfo_ZipCode", "ContactInfo_PhoneDay")) {
            redirectToRoutePath("/ContactInfoAddress");
        }
    }
    
    public render(): JSX.Element {
        return (
            <Router>
                <Routes>
                    <Route path="/VehicleMake" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={30} show={true}/>
                            <VehicleMakeStep model={this.state.model} onUpdateModel={this.handleUpdateModel} setVehicleModels={this.setVehicleModels} vehicleMakes={this.vehicleMakes} />
                        </React.Fragment>
                    } />
                    <Route path="/VehicleModel" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={37} show={true}/>
                            <VehicleModelStep model={this.state.model} onUpdateModel={this.handleUpdateModel} vehicleModels={this.vehicleModels} />
                        </React.Fragment>
                    } />
                    <Route path="/AddVehicle" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={42} show={true}/>
                            <SavePrimaryVehicleData model={this.state.model} />
                            <AddSecondVehicleStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/AdditionalVehicleYear" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={44} show={true}/>
                            <AdditionalVehicleYearStep model={this.state.model} onUpdateModel={this.handleUpdateModel} setVehicleMakes={this.setVehicleMakes} />
                        </React.Fragment>
                    } />
                    <Route path="/AdditionalVehicleMake" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={46} show={true}/>
                            <AdditionalVehicleMakeStep model={this.state.model} onUpdateModel={this.handleUpdateModel} setVehicleModels={this.setVehicleModels} vehicleMakes={this.vehicleMakes} />
                        </React.Fragment>
                    } />
                    <Route path="/AdditionalVehicleModel" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={48} show={true}/>
                            <AdditionalVehicleModelStep model={this.state.model} onUpdateModel={this.handleUpdateModel} vehicleModels={this.vehicleModels} />
                        </React.Fragment>
                    } />
                    <Route path="/InsuranceCarrier" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={50} show={true}/>
                            <SaveAdditionalVehicleData model={this.state.model} />
                            <InsuranceCarrierStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/InsuredSince" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={53} show={true}/>
                            <InsuredSinceStep model={this.state.model} onUpdateModel={this.handleUpdateModel}/>;
                        </React.Fragment>
                    } />
                    <Route path="/Gender" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={56} show={true}/>
                            <SaveCurrentInsuranceData model={this.state.model} />
                            <GenderStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/CreditRating" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={59} show={true} />
                            <CreditHistoryStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/LicenseStatus" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={62} show={true} />
                            <LicenseStatusStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/Incidents" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={65} show={true} />
                            <IncidentsStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/MaritalStatus" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={68} show={true} />
                            <SaveIncidentsData model={this.state.model} />
                            <MaritalStatusStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/IsMilitary" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={71} show={true} />
                            <SavePrimaryDriverData model={this.state.model} />
                            <IsMilitaryStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/Homeowner" element={
                       <React.Fragment>
                            <QuoteFormProgressBar completed={74} show={true} />
                            <HomeownerStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/Bundle" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={77} show={true} />
                            <BundleStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/YearBuilt" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={78} show={true} />
                            <YearBuiltStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/PropertyType" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={79} show={true} />
                            <PropertyTypeStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/AnyClaims" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={82} show={true} />
                            <AnyClaimsStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/FloodInsurance" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={84} show={true} />
                            <InterestedInFloodInsuranceStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/PetInsurance" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={85} show={true} />
                            <InterestedInPetInsuranceStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/BirthYear" element={
                        <React.Fragment>
                            <SaveHomeAddonData model={this.state.model} />
                            <QuoteFormProgressBar completed={86} show={true} />
                            <SaveApplicantInfoData model={this.state.model} />
                            <BirthYearStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/BirthMonth" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={89} show={true} />
                            <BirthMonthStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/BirthDay" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={91} show={true} />
                            <BirthDayStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/ContactInfoName" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={93} show={true} />
                            <SavePrimaryDriverBirthDateData model={this.state.model} />
                            <ContactInfoNameStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/AdditionalDriver" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={94} show={true} />
                            <SaveContactInfoNameData model={this.state.model} />
                            <AdditionalDriverStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/AdditionalDriverBirthYear" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={95} show={true} />
                            <AdditionalDriverBirthYearStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/AdditionalDriverBirthMonth" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={96} show={true} />
                            <AdditionalDriverBirthMonthStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/AdditionalDriverBirthDay" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={97} show={true} />
                            <AdditionalDriverBirthDayStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/AdditionalDriverName" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={98} show={true} />
                            <AdditionalDriverNameStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />
                    <Route path="/ContactInfoAddress" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={99} show={true} />
                            <SaveAdditionalDriverData model={this.state.model} />
                            <ContactInfoAddressStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                        </React.Fragment>
                    } />

                    <Route path="/PreFilledQuote" element={
                        <PreFilledQuoteStep model={this.state.model} onUpdateModel={this.handleUpdateModel} />
                    } />

                    <Route path="/" element={
                        <React.Fragment>
                            <QuoteFormProgressBar completed={22} show={true} />
                            <VehicleYearStep model={this.state.model} onUpdateModel={this.handleUpdateModel} setVehicleMakes={this.setVehicleMakes} />
                        </React.Fragment>
                    } />
                </Routes>
            </Router>
        );
    }
}